"use client";
import dynamic from "next/dynamic";
import { m, useScroll, useTransform } from "framer-motion";
import { useState, useRef, useEffect, useCallback } from "react";
import { twMerge } from "tailwind-merge";
import { useIsDark } from "@/context/ThemeContext";

//components
import Card from "@/components/home/Card";
import CubeComponent from "@/components/home/CubeComponent";
import SectionHeader from "@/components/common/SectionHeader";
import Button from "@/components/common/Button";
import HomeLayout from "@/components/home/layout";

//constants
import { homeCards, expandingCardData, servicesData, coreServices } from "@/constants/Data";

// Code-split the below-the-fold components. These still render on the server —
// their markup must stay in the initial HTML for crawlers and AI tools.
const Approach = dynamic(() => import("@/components/home/Approach"));
const ExpandingCard = dynamic(() => import("@/components/home/ExpandingCard"));
const StatBar = dynamic(() => import("@/components/StatBar"));
const ReviewSlider = dynamic(() => import("@/components/Reviews"));
const ServicesCard = dynamic(() => import("@/components/home/servicesCard"));
const AiNexus = dynamic(() => import("@/components/home/AiNexus"));
const LogoMarquee = dynamic(() => import("@/components/home/LogoMarquee"));

export default function HomePageClient() {
  const isDark = useIsDark();

  // Track if screen is >= 580px (mobile2 breakpoint)
  const [isMobile2OrLarger, setIsMobile2OrLarger] = useState(false);

  const mainCubeRef = useRef(null);
  const cubeTargetRef = useRef(null);
  const cubeContainer = useRef(null);
  const homeCardsRef = useRef(null);

  const [cubeStatus, setCubeStatus] = useState("top");
  const [maxScrollOffset, setMaxScrollOffset] = useState(0);

  const [bottomOffset, setBottomOffset] = useState({ x: 0, y: 0 });

  // Use Framer Motion's useScroll for optimized scroll tracking.
  // The offset is configured to match the original logic (triggering at 25% of viewport height).
  const { scrollYProgress } = useScroll(
    homeCardsRef.current
      ? {
          target: homeCardsRef,
          offset: ["start 25%", "end 25%"], // Start when target top hits 25% down, end when target bottom hits 25% down.
        }
      : {}
  );

  // Transform scroll progress to apply the speed multiplier, preserving the original animation feel.
  const cubeScrollProgress = useTransform(scrollYProgress, (value) => {
    const speedMultiplier = 1.9;
    return value * speedMultiplier;
  });

  const cubeVerticalMovement = useTransform(
    cubeScrollProgress,
    [0, 1],
    [0, maxScrollOffset]
  );

  const updateLayout = useCallback(() => {
    if (
      !mainCubeRef.current ||
      !cubeTargetRef.current ||
      !cubeContainer.current
    )
      return;

    // Batch DOM reads
    const cubeBox = mainCubeRef.current.getBoundingClientRect();
    const targetBox = cubeTargetRef.current.getBoundingClientRect();

    const offsetX = targetBox.left - cubeBox.left;
    const offsetY = targetBox.top - cubeBox.top;

    setBottomOffset({
      x: offsetX,
      y: offsetY,
    });

    if (homeCardsRef.current && mainCubeRef.current) {
      const homeCardsHeight =
        homeCardsRef.current.getBoundingClientRect().height;
      const cubeHeight = mainCubeRef.current.getBoundingClientRect().height;
      const viewportHeight = window.innerHeight;

      let movementRange = homeCardsHeight - cubeHeight;

      const screenWidth = window.innerWidth;

      let maxMovementMultiplier,
        minMovementMultiplier,
        maxPixelLimit,
        minPixelLimit;

      if (screenWidth >= 2000) {
        maxMovementMultiplier = 1.8;
        minMovementMultiplier = 0.6;
        maxPixelLimit = 950;
        minPixelLimit = 300;
      } else if (screenWidth >= 1800) {
        maxMovementMultiplier = 1.4;
        minMovementMultiplier = 0.5;
        maxPixelLimit = 950;
        minPixelLimit = 150;
      } else if (screenWidth >= 1440) {
        maxMovementMultiplier = 1.6;
        minMovementMultiplier = 0.4;
        maxPixelLimit = 900;
        minPixelLimit = 100;
      } else if (screenWidth >= 1024) {
        maxMovementMultiplier = 1.3;
        minMovementMultiplier = 0.5;
        maxPixelLimit = 500;
        minPixelLimit = 220;
      } else if (screenWidth >= 580) {
        maxMovementMultiplier = 1.0;
        minMovementMultiplier = 0.3;
        maxPixelLimit = 1000;
        minPixelLimit = 200;
      } else {
        maxMovementMultiplier = 0.9;
        minMovementMultiplier = 0.3;
        maxPixelLimit = 450;
        minPixelLimit = 200;
      }

      const maxMovement = Math.min(
        viewportHeight * maxMovementMultiplier,
        maxPixelLimit
      );
      const minMovement = Math.min(
        viewportHeight * minMovementMultiplier,
        minPixelLimit
      );

      setMaxScrollOffset(
        Math.max(minMovement, Math.min(movementRange, maxMovement))
      );
    }
  }, []);

  // Measures after mount; skipped below 580px where the cube is hidden (display: none).
  useEffect(() => {
    if (!isMobile2OrLarger) return; // Skip if cube is hidden
    updateLayout();
    window.addEventListener("resize", updateLayout);
    return () => window.removeEventListener("resize", updateLayout);
  }, [updateLayout, isMobile2OrLarger]);

  // Detect screen size to enable the cube's listeners (only on >= 580px)
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile2OrLarger(window.innerWidth >= 580);
    };

    checkScreenSize(); // Check on mount
    window.addEventListener("resize", checkScreenSize);
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  const fadeInUpCards = {
    initial: {
      filter: "saturate(0) contrast(4) brightness(0.1) blur(5px)",
      opacity: 0,
      scale: 0.95,
    },
    whileInView: {
      filter: "none",
      opacity: 1,
      scale: 1,
    },
    viewport: { once: true, amount: 0.4 },
    transition: {
      duration: 0.9,
      ease: [0.25, 0.1, 0.25, 1],
    },
  };
  const fadeInUpHeaders = {
    initial: {
      filter: "saturate(0) contrast(4) brightness(0.1) blur(5px)",
      opacity: 0,
      scale: 0.95,
    },
    whileInView: {
      filter: "none",
      opacity: 1,
      scale: 1,
    },
    viewport: { once: true, margin: "-5% 0px -5% 0px", amount: 0.8 },
    transition: {
      duration: 0.9,
      ease: [0.25, 0.1, 0.25, 1],
    },
  };

  // This useEffect is now much lighter, only toggling a single state for the main cube animation.
  useEffect(() => {
    if (!isMobile2OrLarger) return; // Skip scroll listener if cube is hidden

    const handleScroll = () => {
      const trigger = window.matchMedia("(min-width: 900px)").matches
        ? 200
        : 150;

      if (window.scrollY > trigger && cubeStatus === "top") {
        setCubeStatus("bottom");
      } else if (window.scrollY <= trigger && cubeStatus === "bottom") {
        setCubeStatus("top");
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [cubeStatus, isMobile2OrLarger]);

  return (
    <HomeLayout>
      <div className="relative mt-16 tablet:mt-18 lg:mt-20 laptop:mt-[50px] z-10">
        <main className="flex flex-col justify-center">
          {/* //Home Section */}
          <div className="flex justify-center">
            <div
              className="flex flex-col justify-center w-full max-w-[2000px] mx-5 md:mx-10 xl:mx-[100px] 3xl:mx-[150px]"
              ref={cubeContainer}
            >
              <div className="flex w-full laptop:h-[calc(100vh-50px)] items-center justify-between laptop:min-h-[600px]">
                <m.div
                  className="relative w-full mobile2:w-[50%] text-center mobile2:text-left"
                  initial="hidden"
                  whileInView="show"
                  viewport={{ once: true, amount: 0.75 }}
                  variants={{ hidden: { opacity: 1 }, show: { opacity: 1 } }}
                  style={{
                    willChange: "transform",
                    transform: "translateZ(0)",
                    backfaceVisibility: "hidden",
                    WebkitBackfaceVisibility: "hidden",
                  }}
                >
                  <m.p
                    variants={{
                      hidden: { y: -60, opacity: 0 },
                      show: { y: 0, opacity: 1 },
                    }}
                    transition={{
                      type: "tween",
                      duration: 0.5, // Reduced from 0.8 for faster LCP
                      ease: [0.22, 0.61, 0.36, 1],
                      // Removed delay for LCP optimization
                    }}
                    className="font-alexandria font-extrabold text-[25px] sm:text-[30px] md:text-[35px] tablet:text-[40px] lg:text-[48px] xl:text-[56px] laptop:text-[62px] 3xl:text-[70px] 4xl:text-[77px] uppercase text-secondary mt-[20px] mobile2:mt-0 laptop:mt-[-25px]"
                  >
                    TURN YOUR BUSINESS DATA INTO INTELLIGENCE
                  </m.p>

                  <m.div
                    variants={{
                      hidden: { y: 40, opacity: 0 },
                      show: { y: 0, opacity: 1 },
                    }}
                    transition={{
                      type: "tween",
                      duration: 0.8,
                      ease: [0.22, 0.61, 0.36, 1],
                      delay: 0.06,
                    }}
                    style={{
                      willChange: "transform, opacity",
                    }}
                  >
                    <p
                      className={twMerge(
                        "font-normal text-[10px] mobile2:text-[8px] lg:text-[12px] xl:text-sm mobile2:max-w-9/10 pb-3 xl:pb-9 dark:text-[#D9D9D9] text-[#5A5A6E]",
                      )}
                    >
                      We help organizations connect data,
                      AI and existing systems to improve decisions,
                      automate work and uncover new opportunities.
                    </p>
                    <Button
                      label="Explore AI Nexus"
                      // href="/schedule"
                      href="https://ai-nexus.icieos.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className={twMerge(!isDark && "bg-[#DF27DF]")}
                    />
                  </m.div>
                </m.div>

                <div className="hidden mobile2:block relative w-[50%] pointer-events-none z-10">
                  <div className="flex justify-end items-center">
                    {/* Always server-rendered; the parent's `hidden mobile2:block` hides it below 580px. */}
                    <m.div
                      initial={false}
                      animate={cubeStatus}
                      variants={{
                        top: { x: 0, y: 0, rotate: 0, scale: 1 },
                        bottom: {
                          x: bottomOffset.x,
                          y: bottomOffset.y,
                          rotate: 73,
                        },
                      }}
                      style={{
                        translateY: cubeVerticalMovement,
                      }}
                      transition={{ duration: 0.8, ease: "easeOut" }}
                    >
                      <CubeComponent ref={mainCubeRef} />
                    </m.div>
                  </div>
                </div>
              </div>

              <div className="flex w-full">
                {/* cube's bottom position */}
                <div
                  ref={cubeTargetRef}
                  className="hidden mobile2:flex justify-center w-[48%] tablet:w-[50%] 3xl:w-[55%] opacity-0"
                ></div>
                <div
                  ref={homeCardsRef}
                  className="grid grid-cols-2 auto-rows-fr w-full mobile2:w-[52%] tablet:w-[50%] 3xl:w-[45%] gap-2 mobile:gap-5 mobile2:gap-2 sm:gap-3 tablet:gap-12 xl:gap-10 laptop:gap-[57px] 3xl:gap-16 4xl:gap-x-24 mt-10 lg:mt-16 laptop:mt-0"
                >
                  {homeCards.map((item) => (
                    <m.div
                      key={item.title}
                      {...fadeInUpCards}
                      style={{ willChange: "opacity, transform" }}
                      className="rounded-xl border border-transparent hover:border-[#FF1CF780] hover:shadow-[0_4px_4px_#FF1CF733] transition-all duration-100 ease-in dark:bg-[#FFFFFF0D] bg-[#FFFFFF]"
                    >
                      <Card
                        icon={item.icon}
                        title={item.title}
                        description={item.description}
                      />
                    </m.div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="mt-[50px]">
            <StatBar />
          </div>

          {/* AI Nexus product section */}
          <div className="mt-[50px] xl:mt-[65px]">
            <AiNexus />
          </div>

          {/* partner / technology logo strip */}
          <div className="mt-[50px] xl:mt-[65px]">
            <LogoMarquee />
          </div>

          {/* core services section */}
          <section className="flex flex-col items-center mx-5 md:mx-10 xl:mx-[100px] 3xl:mx-[150]">
            {/* Using separate motion wrappers for title and description. */}
            <m.div
              {...fadeInUpHeaders}
              style={{ willChange: "opacity, transform" }}
            >
              <SectionHeader
                title={"CAPABILITIES BEHIND ENTERPRISE INTELLIGENCE"}
                wrapperClassName="xl:mt-[65px] mb-0 md:mb-0 xl:mb-0 px-0"
              />
            </m.div>

            <m.div
              {...fadeInUpHeaders}
              style={{ willChange: "opacity, transform" }}
            >
              <SectionHeader
                description={
                  "From data foundations to intelligent automation, we build the capabilities organizations need to adopt AI at enterprise scale."
                }
                wrapperClassName="mt-0 md:mt-0 xl:mt-0 xl:mb-[70px] px-0"
                descriptionClassName="xl:mt-[50px] w-full"
              />
            </m.div>

            <div className="grid grid-cols-2 sm:grid-cols-4 w-full max-w-[2000px] gap-2 mobile:gap-x-4 sm:gap-2 tablet:gap-4 xl:gap-[37px]">
              {coreServices.map((item, index) => (
                <m.div
                  key={index}
                  {...fadeInUpCards}
                  style={{ willChange: "opacity, transform" }}
                >
                  <ServicesCard
                    icon={item.icon}
                    name={item.name}
                    alt={item.alt}
                    href={item.href}
                  />
                </m.div>
              ))}
            </div>
          </section>

          {/* Other services section */}
          <section className="flex flex-col items-center mx-5 md:mx-10 xl:mx-[100px] 3xl:mx-[150]">
            {/* Using separate motion wrappers for title and description. */}
            <m.div
              {...fadeInUpHeaders}
              style={{ willChange: "opacity, transform" }}
            >
              <SectionHeader
                title={"TECHNOLOGY & ENGINEERING CAPABILITIES"}
                wrapperClassName="xl:mt-[65px] mb-0 md:mb-0 xl:mb-0 px-0"
              />
            </m.div>

            <m.div
              {...fadeInUpHeaders}
              style={{ willChange: "opacity, transform" }}
            >
              <SectionHeader
                description={
                  "When transformation requires new applications, integrations or infrastructure, our engineering teams provide the technology foundation to make it work."
                }
                wrapperClassName="mt-0 md:mt-0 xl:mt-0 xl:mb-[70px] px-0"
                descriptionClassName="xl:mt-[50px] w-full"
              />
            </m.div>

            <div className="grid grid-cols-2 sm:grid-cols-4 w-full max-w-[2000px] gap-2 mobile:gap-x-4 sm:gap-2 tablet:gap-4 xl:gap-[37px]">
              {servicesData.map((item, index) => (
                <m.div
                  key={index}
                  {...fadeInUpCards}
                  style={{ willChange: "opacity, transform" }}
                >
                  <ServicesCard
                    icon={item.icon}
                    name={item.name}
                    alt={item.alt}
                    href={item.href}
                  />
                </m.div>
              ))}
            </div>
          </section>

          <div className="flex justify-center mx-5 md:mx-10 xl:mx-[100px] 3xl:mx-[150] mt-20 xl:mt-[163px]">
            <Approach />
          </div>

          <section className="flex flex-col items-center mx-5 md:mx-10 xl:mx-[100px] 3xl:mx-[150]">
            {/* Using separate motion wrappers for title and description. */}
            <m.div
              {...fadeInUpHeaders}
              style={{ willChange: "opacity, transform" }}
            >
              <SectionHeader
                title={"INTELLIGENCE BUILT AROUND REAL BUSINESS CHALLENGES"}
                wrapperClassName="xl:mt-25 mb-0 md:mb-0 xl:mb-0 px-0"
              />
            </m.div>

            <m.div
              {...fadeInUpHeaders}
              style={{ willChange: "opacity, transform" }}
            >
              <SectionHeader
                description={
                  "We build tailored digital solutions that address industry-specific needs, helping businesses improve operations, enhance user experiences, and accelerate growth."
                }
                wrapperClassName="mt-0 md:mt-0 xl:mt-0 xl:mb-[70px] px-0"
                descriptionClassName="xl:mt-[50px] w-full"
              />
            </m.div>

            {/* Card slider for small screens */}
            <m.div
              {...fadeInUpCards}
              className="flex w-full md:hidden gap-4 overflow-x-auto snap-x snap-mandatory no-scrollbar"
            >
              {expandingCardData.map((item) => (
                <div
                  key={item.title}
                  className="shrink-0 basis-[75%] small:basis-[58%] mobile2:basis-[45%]"
                >
                  <ExpandingCard
                    icon={item.icon}
                    title={item.title}
                    description={item.description}
                  />
                </div>
              ))}
            </m.div>

            <div className="hidden md:grid grid-cols-3 w-full max-w-[2000px] gap-2 mobile2:gap-3 tablet:gap-5 lg:gap-18 xl:gap-[90px] laptop:gap-[154px] 2xl:gap-[200px] 3xl:gap-[270px]">
              {expandingCardData.map((item) => (
                <m.div
                  key={item.title}
                  {...fadeInUpCards}
                  style={{ willChange: "opacity, transform" }}
                >
                  <ExpandingCard
                    icon={item.icon}
                    title={item.title}
                    description={item.description}
                  />
                </m.div>
              ))}
            </div>
          </section>

          <section className="flex flex-col items-center mx-5 md:mx-10 xl:mx-[100px] 3xl:mx-[150] mb-5">
            {/* Using separate motion wrappers for title and description. */}
            <m.div
              {...fadeInUpHeaders}
              style={{ willChange: "opacity, transform" }}
            >
              <SectionHeader
                title={"What Our Clients Say"}
                wrapperClassName="xl:mt-25 mb-0 md:mb-0 xl:mb-0 px-0"
              />
            </m.div>

            <m.div
              {...fadeInUpHeaders}
              style={{ willChange: "opacity, transform" }}
            >
              <SectionHeader
                description={
                  "Trusted by organizations to turn complex business requirements into practical, high-impact technology solutions."
                }
                wrapperClassName="mt-0 md:mt-0 xl:mt-0 xl:mb-[70px] px-0"
                descriptionClassName="xl:mt-[50px] w-full"
              />
            </m.div>

            <m.div
              initial={{ opacity: 0, y: 30, scale: 0.95 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.9 }}
              className="w-full"
              style={{ willChange: "opacity, transform" }}
            >
              <m.div
                initial={{
                  filter: "saturate(0) contrast(4) brightness(0.1) blur(5px)",
                }}
                whileInView={{ filter: "none" }}
                transition={{ duration: 0.9 }}
              >
                <ReviewSlider />
              </m.div>
            </m.div>
          </section>
        </main>
      </div>
    </HomeLayout>
  );
}
